import type { SVGProps, ComponentType, ReactElement, HTMLAttributes } from 'react';
import { createElement, useState } from 'react';
import classNames from 'classnames';
import { Body } from '@components/text';
import { LazyLoadWrapper } from '@components/utils';
import './ChannelLogoWrapper.scss';

interface ChannelLogoWrapperBaseProps extends HTMLAttributes<HTMLDivElement> {
  /** alt text for the logo, also used as fallback text */
  label: string;
  /** sets size of the bounding box. default large */
  size?: 'small' | 'large';
  /** should image lazy load */
  lazy?: boolean;
  /** optional class */
  className?: string;
}

type ChannelLogoSource =
  | {
      /** source url for the logo */
      imgUrl: string;
      logoElement?: never;
    }
  | {
      /** SVG logo as a component or element */
      logoElement: ComponentType<SVGProps<SVGSVGElement>> | ReactElement;
      imgUrl?: never;
    }
  | {
      /** neither imgUrl nor logoElement provided */
      imgUrl?: never;
      logoElement?: never;
    };

export type ChannelLogoWrapperProps = ChannelLogoWrapperBaseProps & ChannelLogoSource;

/**
 * ChannelLogoWrapper component
 * The logo can be provided as an image URL or as an inline SVG component/element.
 * @param imgUrl - source url for the logo - not used if logoElement is provided
 * @param logoElement - SVG logo as a component or element - not used if imgUrl is provided
 * @param label - alt text for the logo, also used as fallback text
 * @param size - sets size of the bounding box. default large
 * @param lazy - should image lazy load
 * @param className - optional class
 */
export const ChannelLogoWrapper: React.FC<ChannelLogoWrapperProps> = ({
  imgUrl,
  logoElement,
  label,
  size = 'large',
  lazy,
  className,
  ...rest
}) => {
  const hasImage = imgUrl || logoElement;
  const [hasImageError, setHasImageError] = useState(false);

  const FallbackTitle = (
    <Body color="primary" variant="body-01" weight="semibold" size="medium" lineClamp={1}>
      {label}
    </Body>
  );

  return (
    <div
      className={classNames(
        'vds-channel-logo-wrapper',
        `vds-channel-logo-wrapper--${size}`,
        (hasImageError || !hasImage) && 'vds-channel-logo-wrapper--no-image',
        className,
      )}
      {...rest}>
      {imgUrl && (
        <LazyLoadWrapper
          className="vds-channel-logo-wrapper__img"
          src={imgUrl}
          alt={label}
          lazy={lazy}
          fallback={FallbackTitle}
          onImageStateChange={setHasImageError}
        />
      )}
      {logoElement &&
        (typeof logoElement === 'function'
          ? createElement(logoElement as ComponentType<SVGProps<SVGSVGElement>>)
          : logoElement)}
      {!hasImage && FallbackTitle}
    </div>
  );
};
